/* LP - Build Your Protocol — client-side builder/calculator + "Continue" to cart.
   A product can be added several times: `selected` keeps the unique ids in add order and
   `qty` their quantities. Discount tiers count UNITS (2× Vive = 2 products = 10%), the same
   way BOGOS counts cart quantity.
   Public API (used by snippets/lp-byp-list-protocols.liquid):
     window.LpByp.setProtocol(sectionEl|null, ['id1','id2'])  → replace selection (a repeated id = qty 2)
     window.LpByp.add(sectionEl|null, 'id')                   → add one
   Pass null for the section to target the first builder on the page. */
(function () {
  'use strict';

  function money(value, currency) {
    var n = Number(value) || 0;
    return (currency || '$') + n.toFixed(2);
  }

  function parseTiers(root) {
    var raw = root.getAttribute('data-tiers');
    if (raw) {
      try { return JSON.parse(raw); } catch (e) {}
    }
    return [{ min: 2, pct: 10 }, { min: 3, pct: 15 }, { min: 4, pct: 20 }];
  }

  function Builder(root) {
    this.root = root;
    this.currency = root.getAttribute('data-currency') || '$';
    this.subPct = parseFloat(root.getAttribute('data-sub-pct')) || 10;
    this.tiers = parseTiers(root).sort(function (a, b) { return a.min - b.min; });
    this.subscribed = false;
    this.selected = []; // unique ids, in add order
    this.qty = {};      // id → quantity in the protocol (>= 1 for every selected id)
    this.activeTags = {}; // set of selected benefit tags (multi-select, additive/union)
    this._shownToAdd = []; // handles currently shown but not yet in the protocol

    // Build an index of catalog products from card datasets. A card can hold several
    // size-products (grammage); each size is registered so the calculator knows its price.
    this.products = {};
    this.cardEls = [];
    var self = this;
    root.querySelectorAll('.lp-byp-card').forEach(function (card) {
      var title = card.getAttribute('data-byp-title') || '';
      var type = card.getAttribute('data-byp-type') || '';
      var sizes = [];
      var raw = card.getAttribute('data-byp-sizes');
      if (raw) { try { sizes = JSON.parse(raw); } catch (e) {} }
      if (!sizes.length) {
        sizes = [{
          id: card.getAttribute('data-byp-id'),
          label: card.getAttribute('data-byp-variant') || '',
          price: card.getAttribute('data-byp-price') || '',
          compare: card.getAttribute('data-byp-compare') || '',
          thumb: card.getAttribute('data-byp-thumb') || '',
          url: ''
        }];
      }
      card.__sizes = sizes;
      card.__active = 0;
      card.__pendingQty = 1; // stepper value while the card is not in the protocol yet
      self.cardEls.push(card);
      sizes.forEach(function (sz) {
        if (!sz.id) return;
        self.products[sz.id] = {
          id: sz.id,
          title: title,
          type: type,
          price: parseFloat(sz.price) || 0,
          compare: parseFloat(sz.compare) || 0,
          variant: sz.label || '',
          thumb: sz.thumb || '',
          vid: sz.vid || '',        // variant id for /cart/add.js
          sp: sz.sp || ''           // Kaching selling plan id (subscription)
        };
      });
    });

    this.el = {
      meta: root.querySelector('[data-byp="meta"]'),
      slots: root.querySelector('[data-byp="slots"]'),
      progress: root.querySelector('[data-byp="progress"]'),
      rows: root.querySelector('[data-byp="rows"]'),
      tiers: root.querySelectorAll('[data-byp-tier]'),
      summaryNow: root.querySelector('[data-byp="now"]'),
      summaryWas: root.querySelector('[data-byp="was"]'),
      sub: root.querySelector('[data-byp="switch"]'),
      cta: root.querySelector('[data-byp="cta"]'),
      rail: root.querySelector('.lp-byp__rail'),
      handle: root.querySelector('.lp-byp__handle'),
      badge: root.querySelector('[data-byp="badge"]'),
      addAll: root.querySelector('[data-byp="addall"]'),
      addAllCount: root.querySelector('[data-byp="addall-count"]')
    };

    this.bind();
    this.render();
    this.applyFilter();
  }

  Builder.prototype.bind = function () {
    var self = this;

    // Add / remove from catalog cards — uses the card's active size and the stepper quantity
    this.root.querySelectorAll('[data-byp-add-card]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('.lp-byp-card');
        var id = self.activeId(card);
        if (self.qty[id]) self.remove(id);
        else self.addQty(id, card.__pendingQty || 1);
      });
    });

    // Quantity stepper: before adding it sets how many to add; once added it edits the protocol.
    this.cardEls.forEach(function (card) {
      var dec = card.querySelector('[data-byp-qty-dec]');
      var inc = card.querySelector('[data-byp-qty-inc]');
      function step(delta) {
        var id = self.activeId(card);
        if (self.qty[id]) {
          self.setQty(id, self.qty[id] + delta);
        } else {
          card.__pendingQty = Math.min(self.maxQty, Math.max(1, (card.__pendingQty || 1) + delta));
          self.render();
        }
      }
      if (dec) dec.addEventListener('click', function () { step(-1); });
      if (inc) inc.addEventListener('click', function () { step(1); });
    });

    // Size (grammage) selector — build the menu and wire open/select
    this.root.querySelectorAll('[data-byp-size-toggle]').forEach(function (btn) {
      var card = btn.closest('.lp-byp-card');
      var menu = card.querySelector('[data-byp-size-menu]');
      if (!menu) return;
      (card.__sizes || []).forEach(function (sz, idx) {
        var opt = document.createElement('button');
        opt.type = 'button';
        opt.className = 'lp-byp-size__opt';
        opt.textContent = sz.label || ('Size ' + (idx + 1));
        opt.addEventListener('click', function (e) {
          e.stopPropagation();
          self.setActiveSize(card, idx);
          menu.hidden = true;
          btn.setAttribute('aria-expanded', 'false');
        });
        menu.appendChild(opt);
      });
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var willOpen = menu.hidden;
        self.root.querySelectorAll('[data-byp-size-menu]').forEach(function (m) { m.hidden = true; });
        menu.hidden = !willOpen;
        btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });
    });
    document.addEventListener('click', function () {
      self.root.querySelectorAll('[data-byp-size-menu]').forEach(function (m) { m.hidden = true; });
    });

    // Filter chips — multi-select, additive (union). "all" clears the selection.
    this.root.querySelectorAll('.lp-byp__chip').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var f = chip.getAttribute('data-byp-filter') || 'all';
        if (f === 'all') {
          self.activeTags = {};
        } else if (self.activeTags[f]) {
          delete self.activeTags[f];
        } else {
          self.activeTags[f] = true;
        }
        self.syncChips();
        self.applyFilter();
      });
    });

    // Add all shown
    if (this.el.addAll) {
      this.el.addAll.addEventListener('click', function () {
        self._shownToAdd.forEach(function (id) {
          if (!self.qty[id]) { self.selected.push(id); self.qty[id] = 1; }
        });
        self.render();
        self.updateAddAll();
      });
    }

    // Subscribe toggle
    if (this.el.sub) {
      this.el.sub.addEventListener('click', function () {
        self.subscribed = !self.subscribed;
        self.el.sub.setAttribute('aria-checked', self.subscribed ? 'true' : 'false');
        self.render();
      });
    }

    // Mobile sheet handle
    if (this.el.rail) {
      [this.el.handle, this.root.querySelector('.lp-byp__fab')].forEach(function (t) {
        if (t) t.addEventListener('click', function () { self.el.rail.classList.toggle('is-open'); });
      });
    }

    // Continue with Your Protocol → add all selected to cart (BOGOS applies the tier discount)
    if (this.el.cta) {
      this.el.cta.addEventListener('click', function () {
        if (!self.el.cta.disabled) self.checkout();
      });
    }
  };

  Builder.prototype.checkout = function () {
    var self = this;
    if (!this.selected.length) return;
    var items = [];
    this.selected.forEach(function (id) {
      var p = self.products[id];
      if (!p || !p.vid) return;
      var item = { id: Number(p.vid), quantity: self.qty[id] || 1 };
      if (self.subscribed && p.sp) item.selling_plan = Number(p.sp);
      items.push(item);
    });
    if (!items.length) return;
    var cta = this.el.cta;
    cta.classList.add('is-loading');
    cta.disabled = true;
    // Clear the cart first so pressing Continue twice doesn't stack duplicates
    fetch('/cart/clear.js', { method: 'POST', headers: { 'Accept': 'application/json' } })
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items })
        });
      })
      .then(function (r) { return r.json(); })
      .then(function () {
        if (window.LpCart && window.LpCart.open) {
          return window.LpCart.open({ activeElement: cta, source: 'byp-continue' });
        }
        window.location.href = '/cart';
      })
      .catch(function () { window.location.href = '/cart'; })
      .then(function () { cta.classList.remove('is-loading'); cta.disabled = false; });
  };

  Builder.prototype.hasActiveTags = function () {
    for (var k in this.activeTags) { if (this.activeTags[k]) return true; }
    return false;
  };

  // Reflect the active-tag set on the chips (incl. the "All" chip).
  Builder.prototype.syncChips = function () {
    var self = this;
    var any = this.hasActiveTags();
    this.root.querySelectorAll('.lp-byp__chip').forEach(function (c) {
      var f = c.getAttribute('data-byp-filter') || 'all';
      if (f === 'all') c.classList.toggle('is-active', !any);
      else c.classList.toggle('is-active', !!self.activeTags[f]);
    });
  };

  // Show a card when no tag is selected, or when it matches ANY selected tag (union).
  Builder.prototype.applyFilter = function () {
    var self = this;
    var any = this.hasActiveTags();
    this.root.querySelectorAll('.lp-byp-card').forEach(function (card) {
      var show = true;
      if (any) {
        show = false;
        var ct = (card.getAttribute('data-byp-tags') || '').split('|');
        for (var i = 0; i < ct.length; i++) {
          if (self.activeTags[ct[i].trim()]) { show = true; break; }
        }
      }
      card.style.display = show ? '' : 'none';
    });
    this.updateAddAll();
  };

  // "+ Add All Shown (X)" — only while a filter is active and some shown cards aren't added yet.
  Builder.prototype.updateAddAll = function () {
    if (!this.el.addAll) return;
    var self = this;
    var shown = [];
    if (this.hasActiveTags()) {
      this.root.querySelectorAll('.lp-byp-card').forEach(function (card) {
        if (card.style.display === 'none') return;
        var id = card.getAttribute('data-byp-id');
        if (id && !self.qty[id]) shown.push(id);
      });
    }
    this._shownToAdd = shown;
    if (shown.length > 0) {
      this.el.addAll.hidden = false;
      if (this.el.addAllCount) this.el.addAllCount.textContent = shown.length;
    } else {
      this.el.addAll.hidden = true;
    }
  };

  Builder.prototype.activeId = function (card) {
    var s = card.__sizes, i = card.__active || 0;
    if (s && s[i]) return s[i].id;
    return card.getAttribute('data-byp-id');
  };

  // Point the card's live datasets + labels at size `idx` (no selection change).
  Builder.prototype._applySizeAttrs = function (card, idx) {
    var sz = (card.__sizes || [])[idx];
    if (!sz) return;
    card.__active = idx;
    card.setAttribute('data-byp-id', sz.id || '');
    card.setAttribute('data-byp-price', sz.price || '');
    card.setAttribute('data-byp-compare', sz.compare || '');
    card.setAttribute('data-byp-thumb', sz.thumb || '');
    var lbl = card.querySelector('[data-byp-size-label]');
    if (lbl) lbl.textContent = sz.label || '';
    var small = card.querySelector('.lp-byp-card__name small');
    if (small) small.textContent = '(' + (sz.label || '') + ')';
  };

  // User picked a grammage: switch active size, and if the old size was in the protocol swap it
  // (keeping its quantity; if the new size was already there too, the quantities merge).
  Builder.prototype.setActiveSize = function (card, idx) {
    var sizes = card.__sizes || [];
    if (!sizes[idx]) return;
    var oldId = this.activeId(card);
    this._applySizeAttrs(card, idx);
    var newId = sizes[idx].id;
    var pos = this.selected.indexOf(oldId);
    if (pos !== -1 && oldId !== newId) {
      var q = this.qty[oldId] || 1;
      delete this.qty[oldId];
      if (this.qty[newId]) {
        this.selected.splice(pos, 1);
        this.qty[newId] = Math.min(this.maxQty, this.qty[newId] + q);
      } else {
        this.selected[pos] = newId;
        this.qty[newId] = q;
      }
    }
    this.render();
  };

  Builder.prototype.maxQty = 99;

  // Total units in the protocol — what the discount tiers count.
  Builder.prototype.units = function () {
    var self = this;
    return this.selected.reduce(function (sum, id) { return sum + (self.qty[id] || 0); }, 0);
  };

  Builder.prototype.addQty = function (id, n) {
    if (!this.products[id]) return;
    n = Math.max(1, n || 1);
    if (!this.qty[id]) { this.selected.push(id); this.qty[id] = 0; }
    this.qty[id] = Math.min(this.maxQty, this.qty[id] + n);
    this.render();
  };

  // Quantity below 1 keeps 1 — removing is done with "Added" or the ✕ in the panel.
  Builder.prototype.setQty = function (id, n) {
    if (!this.qty[id]) return;
    this.qty[id] = Math.min(this.maxQty, Math.max(1, n));
    this.render();
  };

  Builder.prototype.toggle = function (id) {
    if (this.qty[id]) this.remove(id);
    else this.addQty(id, 1);
  };

  Builder.prototype.remove = function (id) {
    var i = this.selected.indexOf(id);
    if (i === -1) return;
    this.selected.splice(i, 1);
    delete this.qty[id];
    // Reset the card stepper back to 1 for the next add
    this.cardEls.forEach(function (card) {
      (card.__sizes || []).forEach(function (sz) { if (sz.id === id) card.__pendingQty = 1; });
    });
    this.render();
  };

  // A repeated id in `ids` counts as one more unit of that product.
  Builder.prototype.setProtocol = function (ids) {
    var self = this;
    this.selected = [];
    this.qty = {};
    (ids || []).forEach(function (id) {
      if (!self.products[id]) return;
      if (!self.qty[id]) { self.selected.push(id); self.qty[id] = 0; }
      self.qty[id] = Math.min(self.maxQty, self.qty[id] + 1);
    });
    this.render();
  };

  Builder.prototype.add = function (id) { if (!this.qty[id]) this.addQty(id, 1); };

  Builder.prototype.currentTier = function (count) {
    var pct = 0;
    for (var i = 0; i < this.tiers.length; i++) {
      if (count >= this.tiers[i].min) pct = this.tiers[i].pct;
    }
    return pct;
  };

  Builder.prototype.nextTier = function (count) {
    for (var i = 0; i < this.tiers.length; i++) {
      if (count < this.tiers[i].min) return this.tiers[i];
    }
    return null;
  };

  Builder.prototype.render = function () {
    var self = this;
    var count = this.units(); // units, not unique products — matches BOGOS's cart quantity
    var tierPct = this.currentTier(count);

    // Card selected states
    this.cardEls.forEach(function (card) {
      // If a non-active size of this card is the selected one, reflect it on the card.
      var sizes = card.__sizes || [];
      for (var k = 0; k < sizes.length; k++) {
        if (self.qty[sizes[k].id] && k !== card.__active) {
          self._applySizeAttrs(card, k);
          break;
        }
      }
      var id = self.activeId(card);
      var on = !!self.qty[id];
      card.classList.toggle('is-selected', on);
      var q = on ? self.qty[id] : (card.__pendingQty || 1);
      var qv = card.querySelector('[data-byp-qty-val]');
      if (qv) qv.textContent = q;
      var qd = card.querySelector('[data-byp-qty-dec]');
      if (qd) qd.disabled = q <= 1;
      var qi = card.querySelector('[data-byp-qty-inc]');
      if (qi) qi.disabled = q >= self.maxQty;
      var btn = card.querySelector('[data-byp-add-card]');
      if (btn) {
        var label = btn.querySelector('.lp-byp-btn__label');
        var plus = btn.querySelector('.lp-byp-btn__plus');
        if (label) label.textContent = on ? 'Added' : 'Add to Protocol';
        if (plus) plus.textContent = on ? '✓' : '+';
      }
    });

    // Meta line
    if (this.el.meta) {
      if (count === 0) {
        this.el.meta.textContent = 'No products yet — add what you’re working on.';
      } else {
        this.el.meta.textContent = count + (count === 1 ? ' product' : ' products') +
          ' selected' + (tierPct ? ' · ' + tierPct + '% OFF applied' : '');
      }
    }

    // Slots: one per selected product with its quantity badge (min 4, padded with empty "+");
    // scrolls past 4
    if (this.el.slots) {
      var slots = '';
      var slotTotal = Math.max(4, this.selected.length);
      for (var s = 0; s < slotTotal; s++) {
        var id = this.selected[s];
        if (id) {
          var th = this.products[id] && this.products[id].thumb;
          slots += '<div class="lp-byp-slot"' + (th ? ' style="background-image:url(' + th + ')"' : '') + '>' +
            '<span class="lp-byp-slot__count" aria-label="Quantity ' + this.qty[id] + '">' + this.qty[id] + '</span>' +
          '</div>';
        } else {
          slots += '<div class="lp-byp-slot lp-byp-slot--empty">+</div>';
        }
      }
      this.el.slots.innerHTML = slots;
    }

    // Progress to next tier
    if (this.el.progress) {
      var next = this.nextTier(count);
      if (next) {
        var need = next.min - count;
        this.el.progress.textContent = 'Add ' + need + ' more product' + (need === 1 ? '' : 's') +
          ' to unlock ' + next.pct + '% OFF';
        this.el.progress.style.display = '';
      } else {
        this.el.progress.style.display = 'none';
      }
    }

    // Selected rows
    if (this.el.rows) {
      var rows = '';
      this.selected.forEach(function (id) {
        var p = self.products[id];
        var priceStr = money(p.price, self.currency);
        if (p.compare && p.compare > p.price) {
          priceStr = money(p.price, self.currency) + ' <s>' + money(p.compare, self.currency) + '</s>';
        }
        rows += '<div class="lp-byp-row">' +
          '<div class="lp-byp-row__text">' +
            '<div class="lp-byp-row__name">' + p.title +
              ' <span class="lp-byp-row__qty">(' + self.qty[id] + ')</span></div>' +
            '<div class="lp-byp-row__sub">' + (p.type ? p.type + ' | ' : '') + priceStr + ' per item</div>' +
          '</div>' +
          '<button type="button" class="lp-byp-row__remove" data-byp-remove="' + id + '" aria-label="Remove">✕</button>' +
        '</div>';
      });
      this.el.rows.innerHTML = rows;
      this.el.rows.querySelectorAll('[data-byp-remove]').forEach(function (b) {
        b.addEventListener('click', function () { self.remove(b.getAttribute('data-byp-remove')); });
      });
    }

    // Discount tiers highlight — active = highest tier whose min <= count
    var effMin = 0;
    this.tiers.forEach(function (t) { if (count >= t.min) effMin = t.min; });
    this.el.tiers.forEach(function (row) {
      var min = parseInt(row.getAttribute('data-byp-tier'), 10);
      row.classList.toggle('is-active', effMin !== 0 && min === effMin);
    });

    // Summary
    var subtotal = this.selected.reduce(function (sum, id) { return sum + self.products[id].price * self.qty[id]; }, 0);
    var finalTotal = subtotal * (1 - tierPct / 100);
    if (this.subscribed) finalTotal = finalTotal * (1 - this.subPct / 100);
    if (this.el.summaryNow) this.el.summaryNow.textContent = money(finalTotal, this.currency);
    if (this.el.summaryWas) {
      if (finalTotal < subtotal - 0.001) {
        this.el.summaryWas.textContent = money(subtotal, this.currency);
        this.el.summaryWas.style.display = '';
      } else {
        this.el.summaryWas.style.display = 'none';
      }
    }

    // CTA enabled only with selection
    if (this.el.cta) this.el.cta.disabled = count === 0;

    // Mobile badge
    if (this.el.badge) {
      this.el.badge.textContent = count;
      this.el.badge.hidden = count === 0;
    }

    // Keep "Add all shown (X)" in sync with the current selection
    this.updateAddAll();
  };

  // ---- Registry / public API ----
  var builders = [];

  function initAll() {
    document.querySelectorAll('.lp-byp[data-byp-root]').forEach(function (root) {
      if (root.__byp) return;
      var b = new Builder(root);
      root.__byp = b;
      builders.push(b);
    });
  }

  function resolve(sectionEl) {
    if (sectionEl && sectionEl.__byp) return sectionEl.__byp;
    return builders[0] || null;
  }

  window.LpByp = {
    init: initAll,
    setProtocol: function (sectionEl, ids, opts) {
      var b = resolve(sectionEl);
      if (!b) return;
      b.setProtocol(ids);
      opts = opts || {};
      var mobile = window.innerWidth < 992;
      if (opts.focus === 'catalogue') {
        // Open in builder to adjust → show the product grid
        if (b.root.scrollIntoView) b.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (opts.focus === 'panel') {
        // Use this protocol → show the loaded protocol in the panel
        if (mobile && b.el.rail) b.el.rail.classList.add('is-open');
        else if (b.el.rail && b.el.rail.scrollIntoView) b.el.rail.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    add: function (sectionEl, id) { var b = resolve(sectionEl); if (b) b.add(id); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
